import {
	// MeshBasicMaterial,
	MeshPhysicalMaterial,
	// MeshStandardMaterial,
	// MeshPhongMaterial,
	Mesh,
	Vector2,
	Vector3,
	Quaternion,
	Box3,
	CanvasTexture,
	RepeatWrapping,
	// BoxGeometry,
	// SphereGeometry,
	// ShapeGeometry,

	Object3D,
} from 'three';
import {FlakesTexture} from 'three/examples/jsm/textures/FlakesTexture.js';


import {degToRad, radToDeg, getQuaternionByAngle, getLightColorFromTemperature, isString} from '../components/visual/olivio3d-utils';


const ucFirst = (s) => s.substring(0, 1).toUpperCase() + s.substring(1);

const conf = {
	keySep: '__',

	maxHeadsAmount: 8,

	headLinkRegExp: /head_link/i,
	lightLinkRegExp: /light_link/i,
	topLinkRegExp: /top_link/i,

	meshNameSuffix: '_Mesh',
	baseNameSuffix: '_Base',

	metalMaterial: 'steel',
	woodMaterial: 'wood',

	headParts: {
		camera: {
			regExp: /camera/i,
			condition: (head) => head.type === 'camera',
		},
		speaker: {
			regExp: /speaker/i,
			condition: (head) => head.type === 'speaker',
		},
		reflector: {
			regExp: /reflector$/i,
			condition: (head) => head.type === 'luminaire' && head.colourTemperature !== 'RGBW',
		},
		reflectorRgbw: {
			regExp: /reflector_rgbw/i,
			condition: (head) => head.type === 'luminaire' && head.colourTemperature === 'RGBW',
		},
		cap: {
			regExp: /cap$/i,
			condition: (head) => head.type !== 'luminaire' || !(head.accessories?.includes('Cap')),
		},
		longCap: {
			regExp: /cap_long/i,
			condition: (head) => head.type === 'luminaire' && head.accessories?.includes('Cap'),
		}
	},

	defaultHousingSize: 'grande',

	reflectorEmissionIntensity: 0.9,

	defaultLightPower: 1800,
	defaultColourTemperature: '5500K',

	headAngle: 20, // deg

	modelOnCollisionColor: '#AA0000',

	staticViewMaterialColor: 0x404040,
};


class OlivioLuminaire {
	static conf = conf;

	static poleSizes = {};

	static sharedModels = {
		head: {},
	};

	static models = {};

	static sharedModelPatterns = [
		{type: 'head', pattern: /^(?<id>(Grande)|(Medio)|(Piccolo))$/i, storageKeys: ['id']},
	];

	static modelPatterns = [];

	static designName = '';

	static lightLinkOffsets = {};
	static headLinkOffsets = {};
	static internalOffsets = null;

	static requireCutouts = false;
	static hasBrackets = true;
	static singleBracket = false;
	static supportLayouts = true;
	static checkCollisions = true;
	static defaultConfig = {};


	configValues = {};
	pole = null;
	branches = [];
	branchHorizontalOffsets = [];
	branchTargetQuaternions = [];


	constructor(configValues = null) {
		if (configValues !== null) {
			this.update(configValues);
			if (this.constructor.hasBrackets) {
				this.updateHeadsPositions();
			}
		}
	}

	static recognizeModels(models, staticView = false) {
		for (let model of models) {
			if (!('modelType' in model.userData)) {
				const recognizedModel = this.recognizeModel(model);
				if (recognizedModel) {
					model = recognizedModel;
					model.traverse((obj) => {
						if (obj.isMesh) {
							if (obj.geometry) {
								obj.geometry.computeVertexNormals();
							}
							if (!obj.userData.headPart && obj.userData.materialType !== conf.woodMaterial) {
								obj.material = this.getRealisticMaterial(staticView);
							}
							obj.castShadow = true;
							obj.receiveShadow = true;
						}
					});
				}
			}
		}
		if (this.hasBrackets && !this.singleBracket) {
			this.calculateInternalOffsets();
		}
	}

	static recognizeModel(model) {
		let recognizedModel = this.recognizingModel(model, this.sharedModelPatterns, this.sharedModels);
		if (!recognizedModel) {
			recognizedModel = this.recognizingModel(model, this.modelPatterns, this.models);
		}
		return recognizedModel;
	}

	static recognizingModel(model, patterns, store) {
		for (const entry of patterns) {
			const result = this.matchModel(model, patterns);
			if (result) {
				model = this.processModel(model, result.entry, result.matches);
				this.storeModel(store, model, result.entry, result.matches);
				return model;
			}
		}
		return null;
	}

	static matchModel(model, patterns) {
		const name = model.name.trim();
		for (const entry of patterns) {
			const matches = name.match(entry.pattern);
			if (matches) {
				return {
					entry: entry,
					matches: matches
				};
			}
		}
		return false;
	}

	static processModel(model, entry, matches) {
		model.userData.modelType = entry.type;
		model.userData.design = this.designName;
		const keys = [];
		for (const key of entry.storageKeys) {
			const keyIsString = isString(key);
			const keyValue = keyIsString ? key : key.key;
			let value = null;
			if (keyIsString) {
				if (keyValue in matches.groups && matches.groups[keyValue] !== null) {
					value = matches.groups[keyValue].toLowerCase();
				}
			} else {
				value = key.value;
			}
			if (value !== null) {
				model.userData[keyValue] = value;
				keys.push(value);
			}
		}
		const key = keys.join(conf.keySep);
		model.userData.key = key;
		const method = 'process' + ucFirst(entry.type) + 'Model';
		if (method in this) {
			model = this[method](model, entry, matches);
		}
		return model;
	}

	static storeModel(store, model, entry, matches) {
		if (!(entry.type in store)) {
			store[entry.type] = {};
		}
		const keys = [];
		for (const key of entry.storageKeys) {
			const keyIsString = isString(key);
			const keyValue = keyIsString ? key : key.key;
			let value = null;
			if (keyIsString) {
				if (keyValue in matches.groups && matches.groups[keyValue] !== null) {
					value = matches.groups[keyValue].toLowerCase();
				}
			} else {
				value = key.value;
			}
			if (value !== null) {
				keys.push(value);
			}
		}
		const key = keys.join(conf.keySep);
		store[entry.type][key] = model;
	}

	static calculateInternalOffsets() {
		this.internalOffsets = {};
		for (const bracketKey of Object.keys(this.headLinkOffsets)) {
			const bracketEntry = this.headLinkOffsets[bracketKey];
			for (const headKey of Object.keys(this.lightLinkOffsets)) {
				const headEntry = this.lightLinkOffsets[headKey];
				const key = bracketKey + conf.keySep + headKey;
				this.internalOffsets[key] = {
					x: bracketEntry.x, // + headEntry.x,
					y: bracketEntry.y, // + headEntry.y
				};
			}
		}
	}

	static processHeadModel(model, entry, matches) {
		let foundLightLink = false;
		model.userData.hoverable = true;
		model.traverse((subChild) => {
			subChild.userData.hoverable = true;
			if (!foundLightLink && subChild.name.match(conf.lightLinkRegExp)) {
				foundLightLink = true;
				this.lightLinkOffsets[model.userData.key] = {x: subChild.position.x, y: subChild.position.y};
			}

			for (const partName of Object.keys(conf.headParts)) {
				const headPart = conf.headParts[partName];
				if (subChild.name.match(headPart.regExp)) {
					subChild.visible = false;
					subChild.userData.modelType = 'headPart';
					subChild.userData.headPart = partName;
					const materialMethod = 'get' + ucFirst(partName) + 'Material';
					if (materialMethod in this) {
						subChild.material = this[materialMethod]();
					}
				}
			}
		});
		return model;
	}

	static processBracketModel(model, entry, matches) {
		// we need the bracket to be a nested object (object + mesh) in order to simplify the
		// horizontal movement and inclination for conical poles
		if (model.isMesh) {
			const mesh = model;
			model = new Object3D();
			model.name = mesh.name;
			model.position.copy(mesh.position);
			model.userData = {...mesh.userData};
			mesh.position.set(0, 0, 0);
			mesh.name = model.name + conf.meshNameSuffix;
			model.add(mesh);
		}
		let foundHeadLink = false;
		if (!this.singleBracket) {
			model.userData.hoverable = true;
		}
		model.traverse((subChild) => {
			if (!this.singleBracket) {
				subChild.userData.hoverable = true;
			}
			if (!this.singleBracket && !foundHeadLink && subChild.name.match(conf.headLinkRegExp)) {
				foundHeadLink = true;
				this.headLinkOffsets[model.userData.key] = {x: subChild.position.x, y: subChild.position.y};
			}
		});
		return model;
	}


	static getRealisticMaterial(staticView = false) {
		let material;
		if (staticView) {
			material = new MeshPhysicalMaterial({
				clearcoat: 0,
				metalness: 0,
				roughness: 1,
				color: conf.staticViewMaterialColor
			});
		} else {
			const normalMap3 = new CanvasTexture( new FlakesTexture());
			normalMap3.wrapS = RepeatWrapping;
			normalMap3.wrapT = RepeatWrapping;
			normalMap3.repeat.x = 10;
			normalMap3.repeat.y = 6;
			normalMap3.anisotropy = 16;

			material = new MeshPhysicalMaterial({
				clearcoat: 0.5,
				clearcoatRoughness: 0.1,
				metalness: 0.6,
				roughness: 0.5,
				color: 0x666666,
				normalMap: normalMap3,
				normalScale: new Vector2(0.005, 0.005)
			});
			material.userData.colored = true;
		}
		return material;
	}

	static getReflectorMaterial(staticView = false) {
		const material = new MeshPhysicalMaterial({
			clearcoat: 1,
			clearcoatRoughness: 0.25,
			metalness: 0.5,
			roughness: 0.3,
			color: 0xFFFFFF,
		});
		material.emissive.set(0xFFFFFF);
		material.emissiveIntensity = 0;
		return material;
	}

	static getReflectorRgbwMaterial(staticView = false) {
		return this.getReflectorMaterial(staticView);
	}

	static getCameraMaterial(staticView = false) {
		const material = new MeshPhysicalMaterial({
			ior: 2,
			reflectivity: 1,
			iridescence: 0,
			sheen: 0,
			clearcoat: 1,
			clearcoatRoughness: 0,
			metalness: 0,
			roughness: 0,
			specularIntensity: 1,
			specularColor: 0xFFFFFF,
			transmission: 0,
			color: 0x222228,
		});
		// material.emissive.set(0x1F1F2F);
		// material.emissiveIntensity = 0.2;
		return material;
	}

	static getSpeakerMaterial(staticView = false) {
		const material = new MeshPhysicalMaterial({
			ior: 1.6,
			reflectivity: 0,
			iridescence: 0,
			sheen: 0,
			clearcoat: 0.3,
			clearcoatRoughness: 0.8,
			metalness: 0.6,
			roughness: 0.8,
			specularIntensity: 0,
			specularColor: 0xFFFFFF,
			transmission: 0,
			color: 0x3c3c3c,
		});
		return material;
	}


	static getCapMaterial(staticView = false) {
		return this.getRealisticMaterial(staticView);
	}

	static getLongCapMaterial(staticView = false) {
		return this.getRealisticMaterial(staticView);
	}


	static isHeadPartVisible(headPart, headConfigValues) {
		return conf.headParts[headPart].condition(headConfigValues);
	}

	static isHeadSmall(model) {
		return model.userData.id === 'piccolo';
	}


	static getHeadModel(configValues, headConfigValues) {
		const headId = (headConfigValues.housingSize ? headConfigValues.housingSize : (configValues.housingSize ? configValues.housingSize : conf.defaultHousingSize)).toLowerCase().replace('olivio', '').trim();
		return this.sharedModels.head[headId];
	}


	static getPoleModel(configValues) {
		return this.models.pole[this.getPoleId(configValues)];
	}

	static getBracketModel(configValues, index) {
		return this.models.bracket[this.getBracketId(configValues, index)];
	}

	// to be overriden
	static getPoleId(configValues) {}

	// to be overriden
	static getBracketId(configValues, index) {}

	static getPoleColor(configValues) {
		return configValues.color ?? '#909090';
	}

	static getBracketColor(configValues) {
		return configValues.color?? '#909090';
	}

	static getHeadColor(configValues) {
		return configValues.color?? '#909090';
	}

	static getColorForLightTuning(configValues) {
		return this.getPoleColor(configValues);
	}

	static getBracketHorizontalOffset(configValues, height, index) {
		// newB / newH = b / h;
		const entry = this.poleSizes[this.getPoleId(configValues)];
		const newH = entry.maxHeight - height;
		const newB = entry.ratio * newH;
		return newB + entry.xBaseOffset;
	}

	static calculateOffsets(configValues, heads, apply = false) {
		const housingSize = configValues.housingSize ? configValues.housingSize : conf.defaultHousingSize;
		const offsets = [];
		if (!this.internalOffsets) {
			return offsets;
		}
		const miniConf = {...configValues, housingSize: housingSize, heads: heads};
		const poleId = this.getPoleId(miniConf);
		const poleEntry = this.poleSizes[poleId];
		const verticalAngle = degToRad(poleEntry.verticalAngle);
		for (let i = 0; i < heads.length; i++) {
			const head = heads[i];
			const bracketId = this.getBracketId(miniConf, i);
			const singleHousingSize = (head.housingSize ? head.housingSize : housingSize).toLowerCase().replace('olivio', '').trim();
			const verticalOffset = this.calculateVerticalOffset(verticalAngle, bracketId, singleHousingSize);
			const actualHeight = head.height - verticalOffset;
			const la = this.calculateLaValue(poleEntry.height, actualHeight);
			offsets[i] = {
				verticalOffset: Math.round(verticalOffset * 1000) / 1000,
				actualHeight: Math.round(actualHeight * 1000) / 1000,
				la: Math.round(la * 1000) / 1000
			};
			if (apply) {
				head.la = offsets[i].la;
			}
		}
		return offsets;
	}

	static calculateOffsetsByHeight(configValues, height, housingSize, heads, index) {
		const {poleHeight, bracket} = configValues;
		const miniConf = {...configValues, housingSize: housingSize, heads: heads};
		const bracketId = this.getBracketId(miniConf, index);
		const poleId = this.getPoleId(miniConf);
		const poleEntry = this.poleSizes[poleId];
		const verticalAngle = degToRad(poleEntry.verticalAngle);
		housingSize = housingSize.toLowerCase().replace('olivio', '').trim();
		const verticalOffset = this.calculateVerticalOffset(verticalAngle, bracketId, housingSize);
		const actualHeight = height - verticalOffset;
		const la = this.calculateLaValue(poleEntry.height, actualHeight);
		return {
			verticalOffset: Math.round(verticalOffset * 1000) / 1000,
			actualHeight: Math.round(actualHeight * 1000) / 1000,
			la: Math.round(la * 1000) / 1000
		};

	}

	static calculateVerticalOffset(verticalAngle, bracketId, housingSize) {
		const entry = this.internalOffsets[bracketId + conf.keySep + housingSize];
		let verticalOffset = null;
		if (verticalAngle > 0) {
			const a = entry.x * Math.sin(verticalAngle);
			const b = entry.y * Math.cos(verticalAngle);
			verticalOffset = a + b;
		} else {
			verticalOffset = entry.y;
		}
		return verticalOffset;
	}


	// ------------------------------------- INSTANCE METHODS >


	update(configValues) {
		const self = this.constructor;

		// const verticalOffsetsReady = verticalOffsets.isReady();

		// if (design.hasBrackets && !design.singleBracket) {
		// 	if (!verticalOffsetsReady) {
		// 		verticalOffsets.setInternalOffsets(this.internalOffsets);
		// 	}

		this.verticalOffsets = self.calculateOffsets(configValues, configValues.heads);

		// 	if (!verticalOffsetsReady) {
		// 		store.dispatch(setOlivioHeadsData({heads: this.verticalOffsets.map((head) => ({la: head.la}))}));
		// 		return;
		// 	}
		// }

		const poleHeightChanged = this.configValues.poleHeight !== configValues.poleHeight;
		const selectedHeadChanged = this.configValues.selectedHead !== configValues.selectedHead;
		const selectedHeadHeightChanged =
			!selectedHeadChanged &&
			configValues.selectedHead !== null &&
			this.configValues.heads &&
			this.configValues.heads.length &&
			this.configValues.heads[configValues.selectedHead].height !== configValues.heads[configValues.selectedHead].height
		;
		const selectedHeadAngleChanged =
			!selectedHeadChanged &&
			!selectedHeadHeightChanged &&
			configValues.selectedHead !== null &&
			this.configValues.heads &&
			this.configValues.heads.length &&
			this.configValues.heads[configValues.selectedHead].angle !== configValues.heads[configValues.selectedHead].angle
		;
		const flags = {
			poleHeightChanged: poleHeightChanged,
			selectedHeadChanged: selectedHeadChanged,
			selectedHeadHeightChanged: selectedHeadHeightChanged,
			selectedHeadAngleChanged: selectedHeadAngleChanged,
		};

		// pole color only affects floracion
		flags.poleChanged = this.createOrUpdatePole(configValues);
		if (poleHeightChanged || this.isFeatureChanged(configValues, 'color') || this.isFeatureChanged(configValues, 'colorPole')) {
			this.setColor(this.pole, self.getPoleColor(configValues), false);
		}

		if (self.hasBrackets) {
			const poleId = self.getPoleId(configValues);
			const verticalAngle = degToRad(self.poleSizes[poleId].verticalAngle);

			for (let i = 0; i < configValues.heads.length; i++) {
				this.createOrUpdateLightBranch(i, configValues);
				const branchConfig = configValues.heads[i];
				const branch = this.branches[i];
				if (((self.singleBracket && i === 0) || (!self.singleBracket && branchConfig.active)) && branch.bracket.parent !== this.pole) {
					this.pole.add(branch.bracket);
				} else if (!self.singleBracket && !branchConfig.active) {
					// branch.bracket.visible = false;
					if (branch && branch.bracket && branch.bracket.parent === this.pole) {
						this.pole.remove(branch.bracket);
					}
				}
				if (!self.singleBracket) {
					this.branchHorizontalOffsets[i] = self.getBracketHorizontalOffset(configValues, this.verticalOffsets[i].actualHeight, i);
					this.branchTargetQuaternions[i] = getQuaternionByAngle(0, degToRad(branchConfig.angle), verticalAngle);
				}
			}
		}

		// if (!('lumen' in this.configValues) || configValues.lumen !== this.configValues.lumen) {
		// 	const power = this.lightOn ? getLightPower(configValues.lumen, conf.defaultLightPower) : 0;
		// 	for (let i = 0; i < this.lights.length; i++) {
		// 		this.lights[i].power = power;
		// 	}
		// }

		this.configValues = configValues;
		return flags;
	}


	createOrUpdatePole(configValues) {
		const self = this.constructor;
		const model = self.getPoleModel(configValues);
		if (this.pole && this.pole.name === model.name) {
			return false;
		}
		if (this.pole && this.pole.parent) {
			this.pole.parent.remove(this.pole);
			// console.log('POLE DIFF', this.pole.name, model.name);
		}
		const pole = model.clone(true);
		pole.name = model.name;
		this.pole = pole;
		for (let i = 0;  i < this.branches.length; i++) {
			const branch = this.branches[i];
			if (branch.bracket.parent !== this.pole) {
				if (!self.singleBracket || i === 0) {
					this.pole.add(branch.bracket);
				}
			}
		}
		return true;
	}


	createOrUpdateLightBranch(index, configValues) {
		const self = this.constructor;
		const branchConfig = configValues.heads[index];
		const oldBranchConfig = 'heads' in this.configValues ? this.configValues.heads[index] : null;
		if (!branchConfig.active) {
			return;
		}

		const oldBranch = (index < this.branches.length && this.branches[index]) ?
			this.branches[index] : {
				bracket: null,
				bracketMesh: null,
				head: null,
				headLinks: [],
				reflectors: [],
				lightLink: null,
				number: null
			};

		// bracket
		const bracketModel = self.getBracketModel(configValues, index);
		let isNewBracket = false;
		let bracket = null;
		let headLinks = [];
		let bracketMesh = null;
		let bracketSize = null;
		let bracketOffset = null;

		if (
			(!self.singleBracket || index === 0) && (
				!oldBranch.bracket ||
				oldBranch.bracket.name !== bracketModel.name
			)
		) {
			if (oldBranch.bracket && oldBranch.bracket.parent) {
				oldBranch.bracket.parent.remove(oldBranch.bracket);
			}
			isNewBracket = true;
			bracket = bracketModel.clone(true);
			bracket.name = bracketModel.name;

			const originalPos = bracket.position.clone();
			const originalRotation = bracket.rotation.clone();

			if (self.checkCollisions) {
				bracket.position.set(0, 0, 0);
				bracket.rotation.set(0, 0, 0);
				bracket.updateMatrix();
				bracket.updateMatrixWorld();
				if (bracket.geometry) {
					bracket.geometry.computeBoundingBox();
				}
			}
			const bracketBox = new Box3();

			const bracketName = bracket.name;
			const bracketMeshName = (bracketName + conf.meshNameSuffix).toLowerCase();
			if (bracket.isMesh) {
				bracketMesh = bracket;
			}
			// we need to separate the material for each one, otherwise changing the color to one
			// would change the color of all the others
			bracket.traverse((child) => {
				const childName = child.name.toLowerCase();
				// console.log('NAME?', bracketMeshName, childName, childName === bracketMeshName);
				if (!bracketMesh && childName === bracketMeshName) {
					bracketMesh = child;
				} else if (childName.indexOf('head_link') >= 0) {
					headLinks.push(child);
				}
				if (child.isMesh) {
					child.material = self.getRealisticMaterial(configValues.staticView);
				}
			});

			if (self.checkCollisions) {
				const info = this.getBracketCollisionBoxInfo(configValues, bracket, bracketMesh);
				bracketOffset = info.bracketOffset;
				bracketSize = info.bracketSize;
			}

			if (self.checkCollisions) {
				if (oldBranch.bracket) {
					bracket.position.copy(oldBranch.bracket.position);
					bracket.rotation.copy(oldBranch.bracket.rotation);
				} else {
					bracket.position.copy(originalPos);
					bracket.rotation.copy(originalRotation);
				}
			}
		} else {
			if (self.singleBracket && index > 0) {
				bracket = this.branches[0].bracket;
				bracketMesh = this.branches[0].bracketMesh;
				headLinks = this.branches[0].headLinks;
				bracketSize = this.branches[0].bracketSize;
				bracketOffset = this.branches[0].bracketOffset;
			} else {
				bracket = oldBranch.bracket;
				bracketMesh = oldBranch.bracketMesh;
				headLinks = oldBranch.headLinks;
				bracketSize = oldBranch.bracketSize;
				bracketOffset = oldBranch.bracketOffset;
			}
		}

		if (!bracketMesh) {
			console.log('BRACKET MESH NOT FOUND', index, bracket);
		}
		if (!headLinks.length) {
			console.log('HEAD LINK NOT FOUND', bracket);
		}
		bracket.userData.index = self.singleBracket ? 0 : index;

		// head
		const headModel = self.getHeadModel(configValues, branchConfig);
		let head = null;
		let number = null;
		let lightLink = null;
		let reflectors = [];
		let isNewHead = false;
		let headSize = null;
		let headOffset = null;
		const headBox = new Box3();

		if (
			!oldBranch.head ||
			oldBranch.head.name !== headModel.name ||
			!oldBranchConfig ||
			branchConfig.accessories !== oldBranchConfig.accessories ||
			branchConfig.type !== oldBranchConfig.type ||
			branchConfig.colourTemperature !== oldBranchConfig.colourTemperature ||
			configValues.colourTemperature !== this.configValues.colourTemperature
		) {
			isNewHead = true;
			head = headModel.clone(true);
			head.name = headModel.name;
			head.position.set(0, 0, 0);
			head.updateMatrix();
			head.updateMatrixWorld();
			head.traverse((child) => {
				const headPart = child.userData.headPart;
				if (headPart) {
					child.visible = self.isHeadPartVisible(headPart, branchConfig);
					// console.log('HEAD', index, headPart, child.visible);
					const materialMethod = 'get' + headPart.substring(0, 1).toUpperCase() + headPart.substring(1) + 'Material';
					if (materialMethod in self) {
						child.material = self[materialMethod](configValues.staticView);
					}
					if (headPart === 'reflector' || headPart === 'reflectorRgbw') {
						reflectors.push(child);
						// child.material.emissive.set(0xFFFFFF);
						child.material.emissiveIntensity = this.lightOn ? conf.reflectorEmissionIntensity : 0;
					}
				} else {
					child.material = self.getRealisticMaterial(configValues.staticView);
				}

				if (!lightLink && child.name.match(conf.lightLinkRegExp)) {
					lightLink = child;
				}
				child.castShadow = true;
				child.receiveShadow = true;
				if (child.isMesh && child.visible) {
					child.updateMatrix();
					child.updateMatrixWorld();
					child.geometry.computeBoundingBox();
					const box = new Box3();
					box.copy(child.geometry.boundingBox).applyMatrix4(child.matrixWorld);
					headBox.union(box);
				}
			});

			// headBox.expandByScalar(0.01);
			headSize = new Vector3();
			headBox.getSize(headSize);
			headOffset = new Vector3();
			headBox.getCenter(headOffset);
			headOffset.x += self.isHeadSmall(head) ? 0.035 : 0.05;

			if (!lightLink) {
				console.log('NO LINK', index);
			}
			head.rotateZ(degToRad(conf.headAngle));

			number = new Object3D();
			head.add(number);
			number.translateX(0.25);
			number.translateY(0.15);
			number.visible = false;

			// number.rotateY(degToRad(180));
			if (oldBranch.head && oldBranch.head.parent) {
				oldBranch.head.parent.remove(oldBranch.head);
			}
		} else {
			head = this.branches[index].head;
			headSize = this.branches[index].headSize;
			headOffset = this.branches[index].headOffset;
			number = this.branches[index].number;
			lightLink = this.branches[index].lightLink;
			reflectors = this.branches[index].reflectors;
		}
		head.userData.index = index;

		if (self.singleBracket && index > 0) {
			bracketMesh = this.branches[0].bracketMesh;
		}
		if (head.parent !== bracketMesh) {
			if (head.parent) {
				head.parent.remove(head);
			}
			bracketMesh.add(head);
			const headLinkIndex = self.singleBracket ? Math.min(headLinks.length - 1, index) : 0;
			head.position.copy(headLinks[headLinkIndex].position);
			head.rotation.set(0, 0, 0);
			if (self.singleBracket && 'getHeadAngle' in self) {
				const angle = self.getHeadAngle(index, bracket.userData.id);
				head.rotateY(degToRad(angle));
			}
			head.rotateZ(degToRad(conf.headAngle));
		}

		if (
			isNewBracket ||
			isNewHead ||
			!('head' in this.configValues) ||
			configValues.color !== this.configValues.color ||
			configValues.colorInlay !== this.configValues.colorInlay ||
			configValues.heads[index].collisions.length !== this.configValues.heads[index].collisions.length
		) {
			this.setColor(bracket, configValues.heads[index].collisions.length ? conf.modelOnCollisionColor : self.getBracketColor(configValues));
			this.setColor(head, configValues.heads[index].collisions.length ? conf.modelOnCollisionColor : self.getHeadColor(configValues));
		}

		if (
			isNewBracket ||
			isNewHead
		) {
			const colourTemperature = branchConfig.colourTemperature !== null ? branchConfig.colourTemperature : configValues.colourTemperature;
			const lightColor = getLightColorFromTemperature(colourTemperature, conf.defaultColourTemperature);

			for (const reflector of reflectors) {
				reflector.material.emissive.copy(lightColor);
			}
		}

		this.branches[index] = {
			bracket: bracket,
			bracketMesh: bracketMesh,
			bracketSize: bracketSize,
			bracketOffset: bracketOffset,
			headLinks: headLinks,
			head: head,
			headSize: headSize,
			headOffset: headOffset,
			lightLink: lightLink,
			reflectors: reflectors,
			number: number
		};
	}

	getBracketCollisionBoxInfo(configValues, bracket, bracketMesh) {
		const self = this.constructor;
		let bracketOffset = null;
		let bracketSize = null;
		const bracketBox = new Box3();
		bracketBox.setFromObject(bracket);
		bracketSize = new Vector3();
		bracketBox.getSize(bracketSize);
		bracketOffset = new Vector3();
		bracketBox.getCenter(bracketOffset);
		bracketOffset.x -= bracketMesh.position.x;
		return {bracketOffset: bracketOffset, bracketSize: bracketSize};
	}

	updateHeadsPositions(elapsedTime = null) {
		const immediate = elapsedTime === null;
		const self = this.constructor;
		const baseRatio = self.requireCutouts ? 1 : 0.09;
		const translateRatio = immediate ? 1 : Math.min(1, baseRatio *  elapsedTime * 60 / 1000);
		const minDistance = 0.005;

		const rotateRatio = immediate ? 1 : Math.min(1, baseRatio * elapsedTime * 60 / 1000);
		const minAngle = degToRad(1);

		let changed = false;
		for (let i = 0; i < this.branches.length; i++) {
			const branch = this.branches[i];
			if (self.singleBracket) {
				if (i === 0) {
					let headLink = null;
					this.pole.traverse((item) => {
						if (!headLink && item.name.length && item.name.match(conf.topLinkRegExp)) {
							headLink = item;
						}
					});
					if (headLink) {
						branch.bracket.position.y = headLink.position.y;
					} else {
						throw Error('missing top link');
						// branch.bracket.position.y = this.configValues.poleHeight;
					}
				}
			} else {
				const head = this.configValues.heads[i];
				// vertical position
				if (head.active) {
					const offsets = this.verticalOffsets[i];
					if (branch.bracket.position.y !== offsets.actualHeight) {
						// changed = true;
						const finalValue = offsets.actualHeight;
						const currentValue = branch.bracket.position.y;
						const distance = finalValue - currentValue;
						if (Math.abs(distance) <= minDistance) {
							branch.bracket.position.y = finalValue;
						} else {
							branch.bracket.position.y += distance * translateRatio;
							changed = true;
						}
					}
					// horizontal position: the poles may be conical
					const x = this.branchHorizontalOffsets[i];
					if (branch.bracketMesh.position.x !== x) {
						const distance = x - branch.bracketMesh.position.x;
						if (Math.abs(distance) <= minDistance) {
							branch.bracketMesh.position.x = x;
						} else {
							changed = true;
							branch.bracketMesh.position.x += distance * translateRatio;
						}
					}
					// rotation around the pole
					const angleDistance = branch.bracket.quaternion.angleTo(this.branchTargetQuaternions[i]);
					if (radToDeg(angleDistance) > 0.25) {
						changed = true;
						branch.bracket.quaternion.rotateTowards(
							this.branchTargetQuaternions[i],
							angleDistance <= minAngle ? angleDistance : angleDistance * rotateRatio
						);
					}
				}
			}
		}
		return changed;
	}


	isFeatureChanged(configValues, name) {
		return !(name in this.configValues && this.configValues[name] === configValues[name]);
	}


	setColor(parent, color, traverse = true, parts = true) {
		if (traverse) {
			parent.traverse((item) => this.setItemColor(item, color));
		} else {
			this.setItemColor(parent, color);
			if (parts) {
				const modelType = parent.userData.modelType;
				for (const child of parent.children) {
					if (!('modelType' in child.userData) || child.userData.modelType === modelType) {
						this.setItemColor(child, color);
					}
				}
			}
		}
	}


	setItemColor(item, color) {
		if (
			item.isMesh &&
			item.material
		) {
			// objects after boolean operations have an array of materials, instead of one
			const materials = Array.isArray(item.material) ? item.material : [item.material];
			for (const material of materials) {
				if (material.userData && material.userData.colored) {
					material.color.set(color);
				}
			}
			item.castShadow = true;
			item.receiveShadow = true;
		}
	}


	getPole() {
		return this.pole;
	}

	getBranches() {
		return this.branches;
	}

	getConfigValues() {
		return this.configValues;
	}

	getVerticalOffsets() {
		return this.verticalOffsets;
	}
}

export {OlivioLuminaire};

